/**
 * ProofOfDeliveryDialog - Dialog para captura de prova de entrega
 *
 * Combina:
 * - Assinatura digital
 * - Captura de fotos
 * - Nome do recebedor
 * - Observacoes
 * - Documento de identificacao
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Divider,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  Edit as SignatureIcon,
  CameraAlt as PhotoIcon,
  Person as PersonIcon,
  Description as NotesIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Preview as PreviewIcon,
  MyLocation as LocationIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import SignatureCapture from './SignatureCapture';
import PhotoCapture from './PhotoCapture';

// Tabs do dialog
const TAB_SIGNATURE = 0;
const TAB_PHOTOS = 1;
const TAB_DETAILS = 2;
const TAB_PREVIEW = 3;

const ProofOfDeliveryDialog = ({
  open,
  onClose,
  onConfirm,
  stop,
  isLoading = false,
  requireSignature = true,
  requirePhoto = false,
  maxPhotos = 3,
}) => {
  const tokens = usePremiumTokens();

  // Estado
  const [activeTab, setActiveTab] = useState(TAB_SIGNATURE);
  const [signature, setSignature] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [receiverName, setReceiverName] = useState('');
  const [receiverDocument, setReceiverDocument] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  // Capturar geolocalização ao abrir
  useEffect(() => {
    if (open && !location && navigator.geolocation) {
      setGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
          setGettingLocation(false);
        },
        () => {
          setGettingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000 },
      );
    }
  }, [open, location]);

  // Validacao
  const isValid = useCallback(() => {
    if (requireSignature && !signature) return false;
    if (requirePhoto && photos.length === 0) return false;
    return true;
  }, [requireSignature, requirePhoto, signature, photos]);

  // Reset ao fechar
  const handleClose = useCallback(() => {
    setActiveTab(TAB_SIGNATURE);
    setSignature(null);
    setPhotos([]);
    setReceiverName('');
    setReceiverDocument('');
    setNotes('');
    setError(null);
    setLocation(null);
    onClose();
  }, [onClose]);

  // Confirmar entrega
  const handleConfirm = useCallback(() => {
    if (!isValid()) {
      setError('Preencha todos os campos obrigatorios');
      return;
    }

    const proofData = {
      signature,
      photos,
      receiverName: receiverName.trim(),
      receiverDocument: receiverDocument.trim(),
      notes: notes.trim(),
      timestamp: new Date().toISOString(),
      stopId: stop?.id,
      stopName: stop?.name,
      location, // Geolocalização
    };

    if (onConfirm) {
      onConfirm(proofData);
    }
  }, [isValid, signature, photos, receiverName, receiverDocument, notes, stop, onConfirm, location]);

  // Mudar tab
  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setError(null);
  };

  // Indicadores de conclusao
  const hasSignature = Boolean(signature);
  const hasPhotos = photos.length > 0;
  const hasDetails = receiverName.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: tokens?.surface?.base,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CompleteIcon sx={{ color: tokens?.semantic?.success }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
              Prova de Entrega
            </Typography>
            {stop && (
              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
                {stop.name || stop.address}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: `1px solid ${tokens?.border?.subtle}` }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '13px',
            },
            '& .Mui-selected': {
              color: tokens?.accent?.primary,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: tokens?.accent?.primary,
            },
          }}
        >
          <Tab
            icon={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <SignatureIcon fontSize="small" />
                {hasSignature && (
                  <Chip
                    size="small"
                    label="OK"
                    sx={{
                      height: 16,
                      fontSize: '10px',
                      backgroundColor: tokens?.semantic?.success,
                      color: '#fff',
                    }}
                  />
                )}
              </Box>
            }
            label="Assinatura"
            iconPosition="start"
          />
          <Tab
            icon={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhotoIcon fontSize="small" />
                {hasPhotos && (
                  <Chip
                    size="small"
                    label={photos.length}
                    sx={{
                      height: 16,
                      fontSize: '10px',
                      backgroundColor: tokens?.semantic?.success,
                      color: '#fff',
                    }}
                  />
                )}
              </Box>
            }
            label="Fotos"
            iconPosition="start"
          />
          <Tab
            icon={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" />
                {hasDetails && (
                  <Chip
                    size="small"
                    label="OK"
                    sx={{
                      height: 16,
                      fontSize: '10px',
                      backgroundColor: tokens?.semantic?.success,
                      color: '#fff',
                    }}
                  />
                )}
              </Box>
            }
            label="Detalhes"
            iconPosition="start"
          />
          <Tab
            icon={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PreviewIcon fontSize="small" />
                {isValid() && (
                  <Chip
                    size="small"
                    label="✓"
                    sx={{
                      height: 16,
                      fontSize: '10px',
                      backgroundColor: tokens?.semantic?.success,
                      color: '#fff',
                    }}
                  />
                )}
              </Box>
            }
            label="Revisar"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tab: Assinatura */}
        {activeTab === TAB_SIGNATURE && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px', textAlign: 'center' }}>
              Solicite a assinatura do recebedor no campo abaixo
            </Typography>

            <SignatureCapture
              width={360}
              height={180}
              onSignatureChange={setSignature}
              strokeColor={tokens?.text?.primary || '#000000'}
              backgroundColor="#ffffff"
              placeholder="Assine aqui"
              disabled={isLoading}
            />

            {requireSignature && !hasSignature && (
              <Typography sx={{ color: tokens?.semantic?.warning, fontSize: '12px' }}>
                * Assinatura obrigatoria
              </Typography>
            )}
          </Box>
        )}

        {/* Tab: Fotos */}
        {activeTab === TAB_PHOTOS && (
          <Box>
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px', mb: 2 }}>
              Adicione fotos da entrega (comprovante, produto, local)
            </Typography>

            <PhotoCapture
              onPhotosChange={setPhotos}
              maxPhotos={maxPhotos}
              disabled={isLoading}
              initialPhotos={photos}
              placeholder="Adicionar fotos da entrega"
            />

            {requirePhoto && !hasPhotos && (
              <Typography sx={{ color: tokens?.semantic?.warning, fontSize: '12px', mt: 1 }}>
                * Pelo menos uma foto e obrigatoria
              </Typography>
            )}
          </Box>
        )}

        {/* Tab: Detalhes */}
        {activeTab === TAB_DETAILS && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome do recebedor"
              value={receiverName}
              onChange={(e) => setReceiverName(e.target.value)}
              fullWidth
              size="small"
              placeholder="Nome completo"
              disabled={isLoading}
              InputProps={{
                startAdornment: <PersonIcon sx={{ mr: 1, color: tokens?.text?.tertiary }} />,
              }}
            />

            <TextField
              label="Documento (RG/CPF)"
              value={receiverDocument}
              onChange={(e) => setReceiverDocument(e.target.value)}
              fullWidth
              size="small"
              placeholder="Opcional"
              disabled={isLoading}
            />

            <TextField
              label="Observacoes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
              placeholder="Observacoes sobre a entrega..."
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <NotesIcon sx={{ mr: 1, color: tokens?.text?.tertiary, alignSelf: 'flex-start', mt: 1 }} />
                ),
              }}
            />
          </Box>
        )}

        {/* Tab: Preview */}
        {activeTab === TAB_PREVIEW && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px', mb: 1 }}>
              Revise os dados antes de confirmar a entrega
            </Typography>

            {/* Assinatura Preview */}
            <Box sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: tokens?.surface?.elevated,
              border: `1px solid ${tokens?.border?.subtle}`,
            }}
            >
              <Typography sx={{ fontSize: '12px', color: tokens?.text?.tertiary, mb: 1 }}>
                Assinatura
              </Typography>
              {signature ? (
                <Box
                  component="img"
                  src={signature}
                  alt="Assinatura"
                  sx={{
                    maxWidth: '100%',
                    height: 80,
                    objectFit: 'contain',
                    backgroundColor: '#fff',
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Typography sx={{ color: tokens?.semantic?.warning, fontSize: '13px' }}>
                  Nenhuma assinatura capturada
                </Typography>
              )}
            </Box>

            {/* Fotos Preview */}
            <Box sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: tokens?.surface?.elevated,
              border: `1px solid ${tokens?.border?.subtle}`,
            }}
            >
              <Typography sx={{ fontSize: '12px', color: tokens?.text?.tertiary, mb: 1 }}>
                Fotos ({photos.length})
              </Typography>
              {photos.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {photos.map((photo, index) => (
                    <Box
                      key={index}
                      component="img"
                      src={photo.preview || photo}
                      alt={`Foto ${index + 1}`}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: `1px solid ${tokens?.border?.subtle}`,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '13px' }}>
                  Nenhuma foto adicionada
                </Typography>
              )}
            </Box>

            {/* Dados do Recebedor */}
            <Box sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: tokens?.surface?.elevated,
              border: `1px solid ${tokens?.border?.subtle}`,
            }}
            >
              <Typography sx={{ fontSize: '12px', color: tokens?.text?.tertiary, mb: 1 }}>
                Dados do Recebedor
              </Typography>
              <Typography sx={{ fontSize: '14px', color: tokens?.text?.primary, fontWeight: 500 }}>
                {receiverName || '(não informado)'}
              </Typography>
              {receiverDocument && (
                <Typography sx={{ fontSize: '13px', color: tokens?.text?.secondary }}>
                  Doc: {receiverDocument}
                </Typography>
              )}
              {notes && (
                <Typography sx={{ fontSize: '13px', color: tokens?.text?.secondary, mt: 1 }}>
                  Obs: {notes}
                </Typography>
              )}
            </Box>

            {/* Localização */}
            <Box sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: tokens?.surface?.elevated,
              border: `1px solid ${tokens?.border?.subtle}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
            >
              <LocationIcon sx={{ color: location ? tokens?.semantic?.success : tokens?.text?.tertiary }} />
              <Box>
                <Typography sx={{ fontSize: '12px', color: tokens?.text?.tertiary }}>
                  Localização
                </Typography>
                {gettingLocation ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography sx={{ fontSize: '13px', color: tokens?.text?.secondary }}>
                      Obtendo localização...
                    </Typography>
                  </Box>
                ) : location ? (
                  <Typography sx={{ fontSize: '13px', color: tokens?.semantic?.success }}>
                    Capturada ({location.latitude.toFixed(6)}, {location.longitude.toFixed(6)})
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: '13px', color: tokens?.text?.tertiary }}>
                    Não disponível
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Alerta de campos obrigatorios */}
            {!isValid() && (
              <Alert severity="warning">
                Preencha os campos obrigatórios antes de confirmar
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      {/* Resumo */}
      <Box
        sx={{
          px: 2,
          py: 1,
          backgroundColor: tokens?.surface?.elevated,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Chip
          size="small"
          icon={<SignatureIcon />}
          label={hasSignature ? 'Assinado' : 'Pendente'}
          sx={{
            backgroundColor: hasSignature ? `${tokens?.semantic?.success}20` : `${tokens?.semantic?.warning}20`,
            color: hasSignature ? tokens?.semantic?.success : tokens?.semantic?.warning,
          }}
        />
        <Chip
          size="small"
          icon={<PhotoIcon />}
          label={hasPhotos ? `${photos.length} foto(s)` : 'Sem fotos'}
          sx={{
            backgroundColor: hasPhotos ? `${tokens?.semantic?.success}20` : tokens?.surface?.raised,
            color: hasPhotos ? tokens?.semantic?.success : tokens?.text?.tertiary,
          }}
        />
        {hasDetails && (
          <Chip
            size="small"
            icon={<PersonIcon />}
            label={receiverName}
            sx={{
              backgroundColor: `${tokens?.semantic?.success}20`,
              color: tokens?.semantic?.success,
              maxWidth: 150,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        )}
      </Box>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          startIcon={<CloseIcon />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading || !isValid()}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{
            backgroundColor: tokens?.semantic?.success,
            '&:hover': { backgroundColor: '#16a34a' },
          }}
        >
          {isLoading ? 'Salvando...' : 'Confirmar Entrega'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProofOfDeliveryDialog;
